import { useState } from "react";
import { Facebook, Linkedin, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import LanguageToggle from "./LanguageToggle";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useLanguage();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { text: t('nav.about'), href: "/about" },
    { text: t('nav.services'), href: "/services" },
    { text: t('nav.partners'), href: "/partners" },
    { text: t('nav.news'), href: "/news" },
    { text: t('nav.contact'), href: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b shadow-sm">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="/logo.png" 
              alt="Leader Promed Logo" 
              className="h-12 md:h-14"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1 lg:space-x-4">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200"
            >
              {item.text}
            </Link>
          ))}

          <div className="h-6 border-l border-gray-300 mx-2"></div>

          {/* Language Toggle */}
          <LanguageToggle />

          {/* Social Icons */}
          <div className="flex items-center space-x-2 ml-2">
            <a
              href="https://www.facebook.com/profile.php?id=100086261242323"
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700 hover:text-blue-600 transition-colors duration-200"
              aria-label="Facebook"
            >
              <Facebook size={20} />
            </a>
            <a
              href="https://www.linkedin.com/company/leaderpromed"
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700 hover:text-blue-600 transition-colors duration-200"
              aria-label="LinkedIn"
            >
              <Linkedin size={20} />
            </a>
          </div>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 transition-colors duration-200"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          "fixed inset-0 bg-white z-50 md:hidden transition-transform duration-300 ease-in-out",
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Leader Promed Logo"
              className="h-10"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </Link>
          <button
            onClick={toggleMenu}
            className="p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>
        <nav className="flex flex-col p-4 space-y-4">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className="px-4 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              onClick={toggleMenu}
            >
              {item.text}
            </Link>
          ))}

          <div className="border-t border-gray-200 pt-4 mt-2">
            <div className="px-4 py-2">
              <LanguageToggle />
            </div>
            
            {/* Social Icons */}
            <div className="flex items-center space-x-4 px-4 py-2 mt-2">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700 hover:text-blue-600"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700 hover:text-blue-600"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
